import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Body from 'ui/Typography/Body';
import Button from '@ingka/button';
import Title, { HeadingType } from 'ui/Typography/Title';
import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/modal/dist/style.css';
import Modal, { ModalFooter, ModalHeader, ModalBody, Sheets } from '@ingka/modal';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useIntl } from 'react-intl';
import CoverCapsImage from './covercapsimg.jpg';
import { isModalVisible, resetCoverCapsSheet, setDismissed } from './CoverCapsFeature';
import { useAppDispatch } from 'ducks/hooks';
import { getNeededCoverCaps } from './CoverCapsUtils';
import { useCore } from 'contexts/core';
import { getMarketProducts } from 'ducks/products';
import { ProductType } from '@inter-ikea-storageone/range';
import Price from 'ui/Price/Price';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import styled from 'styled-components';
import media from 'ui/media';
import { isMobile } from 'utils/platform';

const Image = styled.img`
  width: 329px;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 5rem;
  padding-top: 5rem;
  ${media.mobile`
    padding-top: 2rem;
  `}
`;

const TitleWrapper = styled.div`
  ${media.mobile`
    width: 100%;
    padding: 0.5rem 0;
  `}
`;

const SpanDivider = styled.span`
  padding: 0 2px;
`;

const CoverCapsSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const coverCapProducts = useMemo(() => {
    return products.filter(p => p.type === ProductType.PAX_COVER_CAPS);
  }, [products]);
  const modalVisible = useSelector(isModalVisible);

  const onDismiss = () => {
    dispatch(setDismissed(true));
  };

  const onAddCoverCaps = () => {
    const coverCapsToAdd = getNeededCoverCaps(coreApi, coverCapProducts);
    Object.keys(coverCapsToAdd).forEach(key => {
      new Array(coverCapsToAdd[key]).fill(key).forEach(productId => {
        const capEntity = coreApi.entities.create(productId);
        capEntity && coreApi.entities.placementService.forcePlaceEntity(capEntity);
      });
    });

    coreApi.history.sendHistoryEvent();
  };

  const onRecalculateCoverCaps = useCallback(() => {
    const coverCapsInScene = coreApi.entities.getByProductType(ProductType.PAX_COVER_CAPS);
    coreApi.entities.deleteMultiple(coverCapsInScene);
    const coverCapsToAdd = getNeededCoverCaps(coreApi, coverCapProducts);
    Object.keys(coverCapsToAdd).forEach(key => {
      new Array(coverCapsToAdd[key]).fill(key).forEach(productId => {
        const capEntity = coreApi.entities.create(productId);
        capEntity && coreApi.entities.placementService.forcePlaceEntity(capEntity);
      });
    });
  }, [coreApi, coverCapProducts]);

  useEffect(() => {
    const onHistoryEvent = () => {
      const coverCapsInScene = coreApi.entities.getByProductType(ProductType.PAX_COVER_CAPS);
      if (coverCapsInScene.length > 0) {
        onRecalculateCoverCaps();
      }
      const coverCapsAfterUpdate = coreApi.entities.getByProductType(ProductType.PAX_COVER_CAPS);
      if (coverCapsInScene.length > 0 && coverCapsAfterUpdate.length === 0) {
        // If user has added cover caps before but does a change that removes all caps we reset the sheet to display it again
        dispatch(resetCoverCapsSheet());
      }
    };

    return coreApi.subscribe('history', onHistoryEvent);
  }, [coreApi, coverCapProducts, dispatch, onRecalculateCoverCaps]);

  const { shoppingItemsWithPrice } = useFetchShoppingProducts(coverCapProducts);

  const totalPrice = useMemo(
    () =>
      Object.entries(getNeededCoverCaps(coreApi, coverCapProducts)).reduce(
        (acc, [id, quantity]) => {
          // The shoppingItem.id field is the local market id. In this context "id" is coreProuctId
          const product = coverCapProducts.find(p => p.coreProductId === id);
          const price = shoppingItemsWithPrice.find(si => si.id === product?.ruItemNo)?.price ?? 0;
          return acc + price * quantity;
        },
        0
      ),
    [coverCapProducts, coreApi, shoppingItemsWithPrice]
  );

  return (
    <Modal data-testid="cover-caps-sheet" visible={!!modalVisible} handleCloseBtn={onDismiss}>
      <Sheets
        title={formatMessage({ id: 'storageone.coverCapsSheetTitle' })}
        header={
          <ModalHeader
            data-testid="modal-header-cover-caps"
            ariaCloseTxt={formatMessage({ id: 'storageone.closeButton' })}
          />
        }
        footer={
          <ModalFooter stacked renderBorder reverse={isMobile()}>
            <Button
              text={formatMessage({ id: 'storageone.coverCapsAddBtn' })}
              onClick={() => {
                onAddCoverCaps();
                dispatch(setDismissed(true));
              }}
              type="primary"
              data-testid="cover-caps-sheet-finalize-btn"
            >
              <SpanDivider />
              {!!totalPrice && <Price amount={totalPrice} />}
            </Button>
            <Button
              text={formatMessage({ id: 'storageone.coverCapsDontAddBtn' })}
              onClick={() => dispatch(setDismissed(true))}
              type="secondary"
              data-testid="cover-caps-sheet-dont-add-btn"
            ></Button>
          </ModalFooter>
        }
      >
        <ModalBody style={{ paddingTop: '0px' }}>
          <TitleWrapper>
            <Title
              mb="l"
              type={ThemeFontStyleTypeEnum.headingMedium}
              heading={HeadingType.HeadingTwo}
            >
              {formatMessage({ id: 'storageone.coverCapsSheetTitle' })}
            </Title>
          </TitleWrapper>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.coverCapsSheetDescription' })}
          </Body>
          <ImageWrapper>
            <Image
              src={CoverCapsImage}
              alt={formatMessage({ id: 'storageone.coverCapsSheetTitle' })}
            />
          </ImageWrapper>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default CoverCapsSheet;
